import React, { useState, useEffect, useRef } from "react";
import styled from "styled-components";
import Masonry from "masonry-layout";
import imagesLoaded from "imagesloaded";
import debounce from "lodash.debounce";
import Experiment from "./Experiments";
import Detail from "./Detail";
import RevealOnScroll from "../../utils/RevealOnScroll";

const ExperimentContainerStyled = styled.div`
  width: 80vw;
  max-width: 1200px;
  margin: 0 auto;
  margin-top: 2rem;

  /* Grid layout for experiment items */
  .grid-sizer,
  .experiment-item {
    width: 31.33%;
    margin-bottom: 2%;
  }
  
  .gutter-sizer {
    width: 3%;
  }

  @media (max-width: 1200px) {
    .grid-sizer,
    .experiment-item {
      width: 47%;
      margin-bottom: 3%;
    }
    .gutter-sizer {
      width: 6%;
    }
  }

  @media (max-width: 768px) {
    width: 90vw;
    .grid-sizer,
    .experiment-item {
      width: 100%;
      margin-bottom: 6%;
    }
    .gutter-sizer {
      width: 0;
    }
  }
`;

const ExperimentContainer = ({ experiments }) => {
  const [selectedExperiment, setSelectedExperiment] = useState(null);
  const gridRef = useRef(null);
  const masonryInstance = useRef(null);

  const initializeMasonry = () => {
    if (masonryInstance.current) {
      masonryInstance.current.destroy();
    }

    masonryInstance.current = new Masonry(gridRef.current, {
      itemSelector: ".experiment-item",
      columnWidth: ".grid-sizer", // reference your .grid-sizer
      gutter: ".gutter-sizer", // reference your .gutter-sizer
      percentPosition: true, // important for responsive % widths
      horizontalOrder: true,
      transitionDuration: 0
    });

    imagesLoaded(gridRef.current, () => {
      masonryInstance.current.layout();
      setTimeout(() => {
        masonryInstance.current.layout();
      }, 100);
    });
  };

  useEffect(() => {
    initializeMasonry();
    return () => {
      if (masonryInstance.current) {
        masonryInstance.current.destroy();
      }
    };
  }, [experiments]);

  // Debounce window resize so we don't spam .layout calls
  useEffect(() => {
    const handleResize = debounce(() => {
      if (masonryInstance.current) {
        masonryInstance.current.layout();
      }
    }, 200);

    window.addEventListener("resize", handleResize);
    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, []);

  const handleExperimentClick = (experiment) => {
    setSelectedExperiment(experiment);
  };

  const handleCloseDetail = () => {
    setSelectedExperiment(null);
  };

  return (
    <>
      <ExperimentContainerStyled ref={gridRef} className="masonry-grid">
        {/* The sizing elements for Masonry: */}
        <div className="grid-sizer" />
        <div className="gutter-sizer" />

        {experiments.map((experiment) => (
          <div key={experiment.id} className="experiment-item">
            <RevealOnScroll 
              threshold={[0.5]} 
              rootMargin="50px 0px"
              triggerOnce={false}
            >
              <Experiment
                className="hoverable"
                experiment={experiment}
                onClick={() => handleExperimentClick(experiment)}
              />
            </RevealOnScroll>
          </div>
        ))}
      </ExperimentContainerStyled>

      {selectedExperiment && (
        <Detail details={selectedExperiment} onClose={handleCloseDetail} />
      )}
    </>
  );
};

export default ExperimentContainer;
